import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X } from 'lucide-react';
import ChatWindow from '../components/FloatingAssistant/ChatWindow';
import { adminApi } from './api';
import type { AdminAssistantAction } from './api';
import type { AssistantChatMessage, ApiAction } from '../components/FloatingAssistant/types';

type Section = 'overview' | 'ai-call-logs' | 'generation-records' | 'users' | 'flow-models' | 'transfer-styles';

const SECTION_LABELS: Record<Section, string> = {
  overview: '概覽',
  'ai-call-logs': 'AI 呼叫記錄',
  'generation-records': '生成記錄',
  users: '使用者管理',
  'flow-models': '工具包管理',
  'transfer-styles': '風格轉換管理',
};

const BUTTON_SIZE = 56;   // w-14 = 3.5rem = 56px
const CHAT_W = 384;       // w-96
const CHAT_H = 540;       // h-[540px]
const DRAG_THRESHOLD = 5; // px

interface AdminFloatingAssistantProps {
  activeSection: Section;
  onNavigate: (section: Section) => void;
  sectionData?: Record<string, any>;
}

const AdminFloatingAssistant: React.FC<AdminFloatingAssistantProps> = ({
  activeSection,
  onNavigate,
  sectionData,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantChatMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const sessionIdRef = useRef<string | undefined>(undefined);

  // 按鈕位置（left, top），初始放在右下角
  const [pos, setPos] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 800;
    const h = typeof window !== 'undefined' ? window.innerHeight : 600;
    return { x: w - 24 - BUTTON_SIZE, y: h - 24 - BUTTON_SIZE };
  });

  // 用 ref 追蹤拖曳狀態，避免 stale closure
  const dragRef = useRef<{
    startMouseX: number;
    startMouseY: number;
    startPosX: number;
    startPosY: number;
    moved: boolean;
  } | null>(null);
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  // 計算 ChatWindow 的位置（貼著按鈕上方，右對齊，夾在 viewport 內）
  const chatWindowStyle = React.useMemo((): React.CSSProperties => {
    const GAP = 12;
    let left = pos.x + BUTTON_SIZE - CHAT_W;
    let top = pos.y - CHAT_H - GAP;
    // 超出底部時改放按鈕上方（如果空間不足則夾到頂邊）
    if (top < 8) top = Math.min(pos.y + BUTTON_SIZE + GAP, window.innerHeight - CHAT_H - 8);
    // 左邊夾
    left = Math.max(8, Math.min(left, window.innerWidth - CHAT_W - 8));
    return { position: 'fixed', left, top };
  }, [pos]);

  // 全域 mousemove / mouseup（在 drag 期間才有作用）
  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!dragRef.current) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragRef.current.startMouseX;
      const dy = clientY - dragRef.current.startMouseY;
      if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
        dragRef.current.moved = true;
      }
      const newX = Math.max(0, Math.min(dragRef.current.startPosX + dx, window.innerWidth - BUTTON_SIZE));
      const newY = Math.max(0, Math.min(dragRef.current.startPosY + dy, window.innerHeight - BUTTON_SIZE));
      setPos({ x: newX, y: newY });
    };

    const onUp = () => {
      if (!dragRef.current) return;
      const wasMoved = dragRef.current.moved;
      dragRef.current = null;
      setIsDragging(false);
      if (!wasMoved) {
        // 視為點擊
        if (isOpenRef.current) {
          setIsOpen(false);
        } else {
          setIsOpen(true);
          setHasUnread(false);
        }
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    // 開啟狀態時不拖曳，讓點擊正常關閉
    if (isOpen) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragRef.current = {
      startMouseX: clientX,
      startMouseY: clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      moved: false,
    };
    setIsDragging(true);
    // 阻止 mousedown 觸發 click 事件（mouseup 後自行處理）
    if ('preventDefault' in e && !('touches' in e)) e.preventDefault();
  };

  const currentPageLabel = SECTION_LABELS[activeSection] ?? activeSection;

  const executeSingleAction = useCallback((action: AdminAssistantAction): string => {
    switch (action.type) {
      case 'navigate':
        if (action.tab && action.tab in SECTION_LABELS) onNavigate(action.tab as Section);
        break;
      case 'hint':
        break;
      case 'flow-model-set-meta':
        window.dispatchEvent(new CustomEvent('admin-assistant:flow-model:set-meta', { detail: action.payload ?? {} }));
        break;
      case 'flow-model-add-preset':
        window.dispatchEvent(new CustomEvent('admin-assistant:flow-model:add-preset', { detail: { presetId: action.presetId } }));
        break;
      case 'flow-model-add-question':
        window.dispatchEvent(new CustomEvent('admin-assistant:flow-model:add-question', { detail: action.question }));
        break;
      case 'flow-model-add-page':
        window.dispatchEvent(new CustomEvent('admin-assistant:flow-model:add-page'));
        break;
      case 'flow-model-set-page':
        window.dispatchEvent(new CustomEvent('admin-assistant:flow-model:set-page', { detail: { page: action.page } }));
        break;
    }
    return action.type === 'hint' ? `提示：${action.message ?? ''}` : action.description;
  }, [onNavigate]);

  const executeActions = useCallback((actions: AdminAssistantAction[]): { executedLabels: string[]; pendingActions: ApiAction[] } => {
    const executedLabels: string[] = [];
    const pendingActions: ApiAction[] = [];
    for (const action of actions) {
      if (action.autoExecute !== false) {
        executedLabels.push(executeSingleAction(action));
      } else {
        pendingActions.push(action as ApiAction);
      }
    }
    return { executedLabels, pendingActions };
  }, [executeSingleAction]);

  const handleExecutePendingAction = (msgId: string, action: ApiAction) => {
    executeSingleAction(action as AdminAssistantAction);
    setMessages(prev => prev.map(msg => {
      if (msg.id !== msgId) return msg;
      const remaining = (msg.pendingActions ?? []).filter(a => a !== action);
      return {
        ...msg,
        pendingActions: remaining.length > 0 ? remaining : undefined,
        executedLabels: [...(msg.executedLabels ?? []), action.description],
      };
    }));
  };

  const handleSend = async (text: string) => {
    const userMsg: AssistantChatMessage = {
      id: `user-${Date.now()}`, role: 'user', content: text, timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsThinking(true);
    try {
      const apiResponse = await adminApi.sendAdminAssistantMessage({
        sessionId: sessionIdRef.current,
        message: text,
        pageContext: { section: activeSection, sectionLabel: currentPageLabel, sectionData },
      });
      sessionIdRef.current = apiResponse.sessionId;
      const { executedLabels, pendingActions } = executeActions(apiResponse.actions);
      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: apiResponse.text,
        executedLabels: executedLabels.length > 0 ? executedLabels : undefined,
        pendingActions: pendingActions.length > 0 ? pendingActions : undefined,
        timestamp: new Date(),
      }]);
      if (!isOpen) setHasUnread(true);
    } catch (err) {
      console.error('[AdminFloatingAssistant] Error:', err);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`, role: 'assistant',
        content: '抱歉，發生了錯誤，請稍後再試。', timestamp: new Date(),
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <>
      <ChatWindow
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        messages={messages}
        isThinking={isThinking}
        onSend={handleSend}
        onExecutePendingAction={handleExecutePendingAction}
        currentPageLabel={currentPageLabel}
        positionStyle={chatWindowStyle}
      />

      {/* 可拖曳浮動按鈕 */}
      <button
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        onClick={isOpen ? () => setIsOpen(false) : undefined}
        style={{ left: pos.x, top: pos.y }}
        className={`fixed z-50 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/40 flex items-center justify-center transition-colors select-none ${
          isDragging ? 'cursor-grabbing scale-95' : isOpen ? 'cursor-pointer' : 'cursor-grab'
        }`}
        title={isOpen ? 'AI 小幫手（關閉）' : 'AI 小幫手（拖曳可移動）'}
      >
        {isOpen ? <X size={22} /> : <Bot size={22} />}
        {hasUnread && !isOpen && (
          <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-slate-950 animate-pulse" />
        )}
      </button>
    </>
  );
};

export default AdminFloatingAssistant;
